import { useRouter } from 'next/navigation'

import { useAuth } from 'src/hooks/useAuth'
import { useCompanyStorage } from 'src/hooks/useCompanyStorage'
import { useError } from 'src/hooks/useError'
import { useGA } from 'src/hooks/useGA'
import { useGetPromiseCompany } from 'src/hooks/useGetPromiseCompany'
import { useGetPromiseProfile } from 'src/hooks/useGetPromiseProfile'
import { useGetPromiseProject } from 'src/hooks/useGetPromiseProject'
import { useProfileRequiredToBeUpdated } from 'src/hooks/useProfileRequiredToBeUpdated'
import { useProfileStorage } from 'src/hooks/useProfileStorage'
import { resolveCompassSsoRedirectUrl } from 'src/libs/helper'
import { Routes } from 'src/routes'
import { IdPassSignInResponse, SamlSignInResponse } from 'src/types'

type AfterLoginActionInput = {
  response: IdPassSignInResponse | SamlSignInResponse
  ssoFrom?: string
  projectId?: string
  to?: string
  isSaml?: boolean
  // 予約管理経由の場合true
  isViaReservation?: boolean
}

export const useLogin = () => {
  const router = useRouter()

  const { saveRequiredAuthenticationInformation } = useAuth()
  const { toErrorEntities } = useError()
  const { saveProfileRequiredToBeUpdated } = useProfileRequiredToBeUpdated()
  const { getProfile } = useGetPromiseProfile()
  const { loadCompany } = useGetPromiseCompany()
  const { loadProject } = useGetPromiseProject()
  const { saveProfileToLocalStorage } = useProfileStorage()
  const { saveCompanyToLocalStorage } = useCompanyStorage()

  const { sendEvent } = useGA()

  /** Id/Pass形式・SAML形式 共通のログイン処理 */
  const afterLoginAction = async (input: AfterLoginActionInput) => {
    const { response, ssoFrom, to, projectId, isSaml, isViaReservation = false } = input
    sendEvent('login')

    await saveRequiredAuthenticationInformation({
      accessToken: response.accessToken,
      accessTokenExpiresAt: new Date(response.accessTokenExpiresAt),
      refreshToken: response.refreshToken,
      refreshTokenExpiresAt: new Date(response.refreshTokenExpiresAt),
      username: response.cognitoUsername,
      usernameExpiresAt: new Date(response.refreshTokenExpiresAt),
    })

    if (ssoFrom === 'compass') {
      const viaReservationQuery = isViaReservation ? '&via_reservation=true' : ''
      if (to) {
        // compassログインページからの遷移の場合、compass側のtoで指定された遷移先に戻す
        const redirectUrl = resolveCompassSsoRedirectUrl(to)
        if (redirectUrl) {
          // toはクエリ付きの場合がある（例: .../presentation/...?referrer=admin）ため、?が二重にならないよう&で連結する
          const tokenSeparator = redirectUrl.includes('?') ? '&' : '?'
          window.location.href = `${redirectUrl}${tokenSeparator}works_token=${response.accessToken}${viaReservationQuery}`
        } else {
          // 許可できないtoの場合はcompass側のダッシュボードページに遷移させる
          window.location.href = `${process.env.RoovCompassUrl}/?works_token=${response.accessToken}${viaReservationQuery}`
        }
      } else if (projectId) {
        // アサイン通知メール内のリンクからの遷移の場合、compass側のプロジェクトページに遷移する（動作としてはプロジェクト一覧の設定ボタンを押下するのと同じURLに遷移する）
        try {
          const project = await loadProject({ accessToken: response.accessToken, projectId })
          window.location.href = `${project.adminUrl}/?works_token=${response.accessToken}${viaReservationQuery}`
        } catch {
          // プロジェクト詳細の取得に失敗した場合はcompass側のダッシュボードページに遷移させる
          window.location.href = `${process.env.RoovCompassUrl}/?works_token=${response.accessToken}${viaReservationQuery}`
        }
      } else {
        // sso_fromのみの場合、compass側のダッシュボードページに遷移させる
        window.location.href = `${process.env.RoovCompassUrl}/?works_token=${response.accessToken}${viaReservationQuery}`
      }
      return
    }

    try {
      const [profile, company] = await Promise.all([
        getProfile(response.accessToken),
        loadCompany(response.accessToken),
      ])
      saveProfileToLocalStorage(profile)
      saveCompanyToLocalStorage(company)

      // 本パスワード設定済みか、SAML認証コールバックからの遷移か、名前入力が終わっているかに応じて遷移先を変える
      if (profile.isResetPassword || (isSaml && !!profile.name)) {
        router.replace(Routes.Dashboard.path)
      } else {
        saveProfileRequiredToBeUpdated(profile)
        if (isSaml && !profile.name) {
          router.replace(Routes.UpdateUserName.path)
          return
        }
        router.replace(Routes.UpdateInitialPassword.path)
      }
    } catch (error) {
      const errors = toErrorEntities(error)
      console.log('errors: ', errors)
    }
  }

  return { afterLoginAction }
}
